// React imports
import { useState } from 'react';
import PropTypes from 'prop-types';

//  material-ui imports
import { Dialog, DialogContent, DialogTitle, DialogActions } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// project imports
import renderFileMngrDialogActions from '../renders';

// third-party imports
import { useIntl } from 'react-intl';

/**
 * FileMngrDialog component.
 * @author Marcos L. A. Soto <marcosleonildo@gmail.com>
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The content of the dialog.
 * @param {string} props.title - The title of the dialog.
 * @param {boolean} props.isOpen - The open state of the dialog.
 * @param {Function} props.onClose - The function to handle the dialog close event.
 * @param {Array} props.buttons - The array of buttons to render in the dialog actions.
 * @returns {React.ReactNode} The FileMngrDialog component.
 */
const FileMngrDialog = ({ children, title, isOpen, onClose, buttons = [] }) => {
    const [open, setOpen] = useState(() => isOpen);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const intl = useIntl();

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Dialog
            fullScreen={fullScreen}
            open={isOpen === undefined ? open : isOpen}
            onClose={onClose}
            aria-labelledby="responsive-dialog-title"
        >
            {title && (
                <DialogTitle id="responsive-dialog-title" px={2}>
                    {title}
                </DialogTitle>
            )}
            <DialogContent>{children}</DialogContent>
            <DialogActions sx={{ marginRight: 2 }}>
                {renderFileMngrDialogActions(buttons)}
                {renderFileMngrDialogActions([
                    {
                        text: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-button-cancel' }),
                        onClick: typeof onClose === 'function' ? onClose : handleClose,
                        variant: 'contained',
                        color: 'primary',
                        disabled: onClose === undefined
                    }
                ])}
            </DialogActions>
        </Dialog>
    );
};

FileMngrDialog.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    buttons: PropTypes.array
};

export default FileMngrDialog;
